import * as React from 'react';
import Map, {FullscreenControl, Marker} from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from '../utils/tokens';
import { setCurrentSpot } from '../store/slices/savedLocationsSlice';
import { useDispatch, useSelector } from 'react-redux';

const Mapbox = ({ width = 600, height = 400 }) => {
    const dispatch = useDispatch();
    const {currentSpot, spots} = useSelector(state => state.savedLocations);
    
    const [viewState, setViewState] = React.useState({
        longitude: -33.84, // Default location (Cape Town)
        latitude: 18.79,
        zoom: 8
    });
    const [loading, setLoading] = React.useState(true);

    // Refs to prevent useEffect conflicts
    const processedLocationRef = React.useRef(null);
    const isManualUpdate = React.useRef(false);

    React.useEffect(() => {
        // Skip if this is a manual update
        if (isManualUpdate.current) {
            isManualUpdate.current = false;
            return;
        }

        // Update viewState from Redux when currentSpot changes
        if (currentSpot && 
           (!processedLocationRef.current || 
            processedLocationRef.current.longitude !== parseFloat(currentSpot.longitude) ||
            processedLocationRef.current.latitude !== parseFloat(currentSpot.latitude))) {

            setViewState(prevState => ({
                ...prevState,
                longitude: parseFloat(currentSpot.longitude),
                latitude: parseFloat(currentSpot.latitude),
            }));
            
            processedLocationRef.current = {
                ...currentSpot,
                longitude: parseFloat(currentSpot.longitude),
                latitude: parseFloat(currentSpot.latitude)
            };
            setLoading(false);
        } else if (!currentSpot) {
            // Get user's current location if no currentSpot
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setViewState(prevState => ({
                            ...prevState,
                            longitude: position.coords.longitude,
                            latitude: position.coords.latitude
                        }));
                        setLoading(false);
                    },
                    (error) => {
                        console.warn('Error getting user location:', error);
                        setLoading(false);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 60000
                    }
                );
            } else {
                console.warn('Geolocation is not supported by this browser');
                setLoading(false);
            }
        }
    }, [currentSpot]);

    const handleMapClick = (event) => {
        const { lng, lat } = event.lngLat;
        
        dispatch(setCurrentSpot({
            longitude: lng,
            latitude: lat
        }));
    };

    const handleSavedLocationClick = (e, loc) => {
        e.stopPropagation();
        
        // Prevent useEffect interference
        isManualUpdate.current = true;
        
        // Update viewState immediately
        setViewState(prevState => ({
            ...prevState,
            longitude: parseFloat(loc.longitude),
            latitude: parseFloat(loc.latitude),
        }));
        
        // Update ref to prevent conflicts
        processedLocationRef.current = {
            longitude: parseFloat(loc.longitude),
            latitude: parseFloat(loc.latitude),
            name: loc.name,
            id: loc.id
        };
        
        // Update Redux state
        dispatch(setCurrentSpot({
            longitude: parseFloat(loc.longitude),
            latitude: parseFloat(loc.latitude),
            name: loc.name,
            id: loc.id
        }));
    };

    if (loading) {
        return (
            <div style={{
                width, 
                height, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f0f8ff',
                border: '1px solid #ddd',
                borderRadius: '4px'
            }}>
                Loading surf map...
            </div>
        );
    }

    return (
        <Map
            mapboxAccessToken={MAPBOX_TOKEN}
            {...viewState}
            onMove={evt => setViewState(evt.viewState)}
            style={{width, height}}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            onClick={handleMapClick}
        >
            <FullscreenControl />
            
            {/* Current selected location marker */}
            {currentSpot && (
                <Marker
                    longitude={currentSpot.longitude}
                    latitude={currentSpot.latitude}
                    anchor="bottom"
                >
                    <div style={{
                        backgroundColor: currentSpot.name ? '#0038a4' : '#FF6B6B',
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        border: '2px solid white',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }} />
                </Marker>
            )}
            
            {/* Saved spots markers */}
            {spots && spots.map((loc, index) => (
                <Marker
                    key={index}
                    longitude={loc.longitude}
                    latitude={loc.latitude}
                    anchor="bottom"
                >
                    <div 
                        style={{
                            backgroundColor: '#3f9cff',
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            border: '2px solid white',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                        onClick={(e) => handleSavedLocationClick(e, loc)}
                    />
                </Marker>
            ))}
        </Map>
    );
};

export default Mapbox;